/**
 * Town Crier Component
 *
 * Displays Machina updates/changelog in a medieval-themed scrollwork parchment.
 * Shows on first load after version change, dismissible with localStorage tracking.
 */

import { useState, useEffect } from 'react';
import { Icon } from './Icon';
import { GouldCorner } from './Ornaments';
import { MACHINA_VERSION } from '@machina/core';

// Changelog entries - newest first
const CHANGELOG: ChangelogEntry[] = [
  {
    version: '0.2.0',
    date: '2026-08-22',
    title: 'The Ledger Opens',
    entries: [
      { type: 'feature', text: 'Campaigns now pick a game system — the rules travel with the campaign, and the AI GM plays by them.' },
      { type: 'feature', text: 'Early look: The Long Indenture — a dying-empire dark fantasy world running on Doomsworn, a lethal and light d20 system with a Doom Die and a Pact Ledger of Debt, Liens and Callings. Rough edges expected.' },
      { type: 'feature', text: 'New Doomsworn chat commands: /doom rolls against the clock, /draw pulls from the deck.' },
      { type: 'improvement', text: 'The GM remembers where you are — world state is rebuilt from the whole session, so it stops putting the party back somewhere you left an hour ago.' },
      { type: 'improvement', text: 'The chat box lights up when the GM is waiting on you, so a turn never stalls in silence.' },
      { type: 'fix', text: 'A new character joins the party immediately, and the AI GM sees characters created outside the chat.' },
      { type: 'fix', text: 'Map generation falls back to a working provider when one fails, instead of returning nothing.' },
      { type: 'fix', text: 'Advantage and disadvantage are honoured on checks and attacks.' },
      { type: 'fix', text: 'The New Campaign button sat 5,800px below the fold in the lobby — it is back where you can reach it.' },
    ],
  },
  {
    version: '0.1.1',
    date: '2026-07-16',
    title: 'The Solo Loop Closes',
    entries: [
      { type: 'feature', text: 'Streaming narration — the GM talks while it thinks instead of making you wait for the whole turn.' },
      { type: 'feature', text: 'End Turn button — the monster-turn loop in solo combat is finally reachable.' },
      { type: 'feature', text: 'Solo mode is open to everyone for the beta.' },
      { type: 'improvement', text: 'Worlds gallery on the home screen, and /worlds resolves to a real page.' },
      { type: 'improvement', text: 'Player token art is drawn from race and class rather than the character name.' },
      { type: 'fix', text: 'Solo sessions resume where you left off instead of re-introducing themselves.' },
      { type: 'fix', text: 'Isometric view — readable ambient light by default, and characters face forward.' },
      { type: 'fix', text: 'Starting combat no longer crashes on a partly-built encounter.' },
      { type: 'fix', text: 'Seven fixes from a full solo-mode audit and end-to-end run.' },
    ],
  },
  {
    version: '0.1.0',
    date: '2026-05-30',
    title: 'Welcome to the Beta',
    entries: [
      { type: 'feature', text: 'Welcome, beta testers! You\'re among the first to run real sessions in Machina — thank you for helping shape it.' },
      { type: 'feature', text: 'Found a bug or something odd? Click the bug button in the bottom-right — it captures a screenshot and console logs and sends them straight to us.' },
      { type: 'feature', text: 'New here? Create a campaign, then run a one-shot in The Tethered City to see a full session from start to finish.' },
      { type: 'improvement', text: 'After your first session, tell us what you think — what felt like magic, and what felt broken. Your feedback steers what we build next.' },
    ],
  },
  {
    version: '0.0.13',
    date: '2026-02-16',
    title: 'Lady E\'s Gambit',
    entries: [
      { type: 'feature', text: 'Player card system — GM deals mysterious cards to players with target hints' },
      { type: 'feature', text: 'Raptor Dash solo betting — 100gp purse, persistent winnings, BUSTED animation' },
      { type: 'feature', text: 'Street Vendor Specials — dodgy solo cards with 35% backfire chance' },
      { type: 'feature', text: 'Session-coupled card seeding — cards auto-load when a session begins' },
      { type: 'improvement', text: 'Connection resilience — unified realtime subscriptions with auto-reconnect' },
      { type: 'improvement', text: 'Connection indicator — green/yellow/red dot shows realtime status' },
      { type: 'improvement', text: 'Mob token generation — FLUX-generated creature art with localStorage cache' },
      { type: 'fix', text: 'Fixed iframe covering Back to Map button in mini-games' },
      { type: 'fix', text: 'Fixed solo mode channel crashes when no multiplayer room active' },
    ],
  },
  {
    version: '0.0.12',
    date: '2026-02-12',
    title: 'The GM\'s Arsenal',
    entries: [
      { type: 'feature', text: 'Sound & Foley board — mood music, 12 ambience layers, 15 SFX triggers for the GM' },
      { type: 'feature', text: 'Session Plan runner — structured one-shot adventures with scene flow and clue tracking' },
      { type: 'feature', text: 'Chase tracker — progress vs failures, complication tables, per-ability check options' },
      { type: 'feature', text: 'GM Assistant popup window — separate reference window with session plans and NPC list' },
      { type: 'feature', text: 'Character sheet popup windows with BroadcastChannel dice relay back to chat' },
      { type: 'feature', text: 'Spell combat pipeline — save DCs, damage rolls, healing rolls, cantrip scaling' },
      { type: 'improvement', text: 'Campaign data isolation — characters scoped to campaigns with mismatch guard' },
      { type: 'improvement', text: 'Dice sounds — procedural audio on every roll, togglable from Foley board' },
      { type: 'fix', text: 'Fixed infinite dice re-roll loop bug' },
      { type: 'fix', text: 'Campaign map toggle re-enabled as local-only (no more view jumping)' },
    ],
  },
  {
    version: '0.0.11',
    date: '2026-02-07',
    title: 'The NPCs Speak',
    entries: [
      { type: 'feature', text: 'NPC dialogue in chat — GM can speak as any campaign NPC' },
      { type: 'feature', text: 'NPC speaker selector with faction-colored dropdown' },
      { type: 'feature', text: 'Dialogue hint chips — click an NPC\'s prepared lines to send' },
      { type: 'improvement', text: 'NPC messages styled with faction colors, Georgia serif, and quoted text' },
      { type: 'improvement', text: 'Full Supabase persistence for NPC chat messages' },
    ],
  },
  {
    version: '0.0.10',
    date: '2026-02-06',
    title: 'Keystone NPCs',
    entries: [
      { type: 'feature', text: 'AI-powered NPC import — paste text or a URL, AI extracts characters' },
      { type: 'feature', text: 'Keystone NPC panel in Story tab with search, filters, and status tracking' },
      { type: 'feature', text: 'Seed NPCs from faction lore with one click' },
      { type: 'feature', text: 'NPC cards with disposition, dialogue hints, GM notes, and secret flag' },
      { type: 'improvement', text: 'Expandable NPC cards with inline editing and status management' },
      { type: 'improvement', text: 'Supabase realtime sync for campaign NPCs' },
    ],
  },
  {
    version: '0.0.9',
    date: '2026-02-04',
    title: 'The Tethered City',
    entries: [
      { type: 'feature', text: 'New campaign world: The Tethered City — a city suspended between planes, held together by belief' },
      { type: 'feature', text: 'Faction reputation system with 6 philosophical factions and ideology compass' },
      { type: 'feature', text: 'Psyche voice system — AI-suggested character thoughts curated by the GM' },
      { type: 'feature', text: '7 districts, 14 NPCs, 3 starting scenarios, and 7 quest hooks' },
      { type: 'feature', text: 'Tethered City playset for campaign creation wizard' },
      { type: 'improvement', text: 'Ideology compass tracks character alignment on Order/Entropy and Self/Collective axes' },
    ],
  },
  {
    version: '0.0.8',
    date: '2026-01-29',
    title: 'Raptor Dash!',
    entries: [
      { type: 'feature', text: 'Raptor Dash mini-game - race dinosaurs through the jungle!' },
      { type: 'feature', text: 'Post-race encounters: bandits ambush the winner, pirates raid the docks' },
      { type: 'feature', text: 'AI-generated battle maps via FLUX for dynamic combat arenas' },
      { type: 'feature', text: 'Battle music & sound effects: epic orchestral soundtrack, sword clashes, spell impacts' },
      { type: 'improvement', text: 'Multiple encounter types: undead, pirates, dinosaurs, yuan-ti, cultists' },
      { type: 'improvement', text: 'Token images persist to cache - no more regenerating good ones!' },
    ],
  },
  {
    version: '0.0.6',
    date: '2026-01-17',
    title: 'AI-Generated Token Art',
    entries: [
      { type: 'feature', text: 'AI-generated character portraits via FLUX' },
      { type: 'feature', text: 'AI-generated monster tokens (goblin, skeleton, orc, etc.)' },
      { type: 'feature', text: 'AI-generated condition icons (all 5e conditions)' },
      { type: 'improvement', text: 'Tokens now display character/monster artwork' },
      { type: 'improvement', text: 'SRD monsters auto-match to token images' },
    ],
  },
  {
    version: '0.0.5',
    date: '2026-01-16',
    title: 'Campaign Invites & Onboarding',
    entries: [
      { type: 'feature', text: 'Invite players via shareable links or email tracking' },
      { type: 'feature', text: 'Join page with campaign preview before accepting' },
      { type: 'feature', text: 'Open Tasks panel guides new players through setup' },
      { type: 'feature', text: 'DM invite manager: create, view, copy links, revoke' },
      { type: 'improvement', text: 'Auto-copy invite link to clipboard on create' },
      { type: 'improvement', text: 'Character wizard completion marks task done' },
    ],
  },
  {
    version: '0.0.4',
    date: '2026-01-14',
    title: 'GM Taste Interview',
    entries: [
      { type: 'feature', text: 'Campaign creation wizard with playset shortcuts' },
      { type: 'feature', text: 'Choose fun types, tone, and moral physics' },
      { type: 'feature', text: 'Configure setting: magic level, tech era' },
      { type: 'feature', text: 'Set group patron and party bond' },
      { type: 'feature', text: 'SRD content: classes, races, feats, backgrounds' },
      { type: 'improvement', text: 'Quick Start option for GMs in a hurry' },
    ],
  },
  {
    version: '0.0.3',
    date: '2026-01-13',
    title: 'Campaign Management',
    entries: [
      { type: 'feature', text: 'Create new campaigns from profile or sidebar' },
      { type: 'feature', text: 'Campaign switcher in left sidebar' },
      { type: 'feature', text: 'Town Crier announces updates (you\'re looking at it!)' },
      { type: 'improvement', text: 'Critical hits auto-roll double damage' },
      { type: 'improvement', text: 'Monster behavior notes for The Deep tactics' },
    ],
  },
  {
    version: '0.0.2',
    date: '2026-01-13',
    title: 'Battle Simulator',
    entries: [
      { type: 'feature', text: 'Auto-combat battle simulation' },
      { type: 'feature', text: 'Monster attention & targeting via The Deep' },
      { type: 'feature', text: 'Token movement during combat' },
      { type: 'improvement', text: 'Dead tokens show skull overlay' },
    ],
  },
  {
    version: '0.0.1',
    date: '2026-01-11',
    title: 'Initial Release',
    entries: [
      { type: 'feature', text: 'Virtual tabletop with map & tokens' },
      { type: 'feature', text: '3D dice rolling' },
      { type: 'feature', text: 'Real-time multiplayer sync' },
      { type: 'feature', text: 'Character & monster management' },
    ],
  },
];

interface ChangelogEntry {
  version: string;
  date: string;
  title: string;
  entries: { type: 'feature' | 'improvement' | 'fix'; text: string }[];
}

interface TownCrierProps {
  /** Force show regardless of localStorage (for testing) */
  forceShow?: boolean;
  /** Callback when dismissed */
  onDismiss?: () => void;
}

const GOLD = '#d4af37';
const PARCHMENT = '#f4e4bc';
const PARCHMENT_DARK = '#d4c4a0';
const INK = '#2a1810';

export function TownCrier({ forceShow = false, onDismiss }: TownCrierProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (forceShow) {
      setIsVisible(true);
      setIsAnimating(true);
      return;
    }

    // Check localStorage for last seen version
    const lastSeenVersion = localStorage.getItem('machina-last-version');
    if (lastSeenVersion !== MACHINA_VERSION) {
      setIsVisible(true);
      setTimeout(() => setIsAnimating(true), 50);
    }
  }, [forceShow]);

  const handleDismiss = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      localStorage.setItem('machina-last-version', MACHINA_VERSION);
      onDismiss?.();
    }, 300);
  };

  if (!isVisible) return null;

  const latestEntry = CHANGELOG[0];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 3000,
        opacity: isAnimating ? 1 : 0,
        transition: 'opacity 0.3s ease-out',
      }}
      onClick={handleDismiss}
    >
      {/* Parchment scroll */}
      <div
        style={{
          position: 'relative',
          width: 480,
          maxWidth: '90vw',
          maxHeight: '80vh',
          background: `linear-gradient(180deg, ${PARCHMENT} 0%, ${PARCHMENT_DARK} 100%)`,
          borderRadius: 8,
          padding: 'var(--spacing-xl)',
          boxShadow: `
            0 20px 60px rgba(0, 0, 0, 0.5),
            0 0 40px rgba(212, 175, 55, 0.2),
            inset 0 0 60px rgba(42, 24, 16, 0.1)
          `,
          border: `3px solid ${GOLD}`,
          overflow: 'hidden',
          transform: isAnimating ? 'scale(1)' : 'scale(0.9)',
          transition: 'transform 0.3s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Corner ornaments */}
        <GouldCorner
          size={32}
          color={GOLD}
          style={{ position: 'absolute', top: 8, left: 8 }}
        />
        <GouldCorner
          size={32}
          color={GOLD}
          style={{ position: 'absolute', top: 8, right: 8, transform: 'scaleX(-1)' }}
        />
        <GouldCorner
          size={32}
          color={GOLD}
          style={{ position: 'absolute', bottom: 8, left: 8, transform: 'scaleY(-1)' }}
        />
        <GouldCorner
          size={32}
          color={GOLD}
          style={{ position: 'absolute', bottom: 8, right: 8, transform: 'scale(-1)' }}
        />

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-lg)' }}>
          <div
            style={{
              fontSize: 12,
              fontFamily: 'var(--font-display)',
              color: INK,
              opacity: 0.6,
              textTransform: 'uppercase',
              letterSpacing: 2,
              marginBottom: 4,
            }}
          >
            Hear Ye, Hear Ye!
          </div>
          <h2
            style={{
              margin: 0,
              fontSize: 28,
              fontFamily: 'var(--font-display)',
              color: INK,
              fontWeight: 400,
              letterSpacing: 1,
            }}
          >
            Machina v{latestEntry.version}
          </h2>
          <div
            style={{
              fontSize: 14,
              fontFamily: 'var(--font-display)',
              color: INK,
              opacity: 0.7,
              marginTop: 4,
            }}
          >
            {latestEntry.title}
          </div>
        </div>

        {/* Divider */}
        <div
          style={{
            height: 2,
            background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
            marginBottom: 'var(--spacing-lg)',
          }}
        />

        {/* Changes list - scrollable with hidden scrollbar */}
        <div
          style={{
            marginBottom: 'var(--spacing-lg)',
            maxHeight: '40vh',
            overflowY: 'auto',
            paddingRight: 'var(--spacing-sm)',
            // Hide scrollbar but keep functionality
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE/Edge
          }}
          className="town-crier-scroll"
        >
          <style>{`
            .town-crier-scroll::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          {CHANGELOG.map((release, releaseIndex) => (
            <div key={release.version} style={{ marginBottom: 'var(--spacing-lg)' }}>
              {/* Version header (skip for first/current) */}
              {releaseIndex > 0 && (
                <div
                  style={{
                    fontSize: 13,
                    fontFamily: 'var(--font-display)',
                    color: INK,
                    opacity: 0.6,
                    marginBottom: 'var(--spacing-sm)',
                    paddingTop: 'var(--spacing-sm)',
                    borderTop: `1px solid rgba(42, 24, 16, 0.2)`,
                  }}
                >
                  v{release.version} — {release.title}
                </div>
              )}
              {release.entries.map((entry, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'var(--spacing-sm)',
                    marginBottom: 'var(--spacing-sm)',
                    fontSize: 14,
                    fontFamily: 'var(--font-body)',
                    color: INK,
                    opacity: releaseIndex === 0 ? 1 : 0.7,
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor:
                        entry.type === 'feature'
                          ? 'rgba(45, 138, 78, 0.2)'
                          : entry.type === 'improvement'
                          ? 'rgba(212, 175, 55, 0.3)'
                          : 'rgba(220, 20, 60, 0.2)',
                      color:
                        entry.type === 'feature'
                          ? '#2d8a4e'
                          : entry.type === 'improvement'
                          ? '#8b6914'
                          : '#8b0000',
                      fontSize: 12,
                      flexShrink: 0,
                    }}
                  >
                    {entry.type === 'feature' ? (
                      <Icon name="sparkle" size={12} />
                    ) : entry.type === 'improvement' ? (
                      <Icon name="arrow-up" size={12} />
                    ) : (
                      <Icon name="check" size={12} />
                    )}
                  </span>
                  <span>{entry.text}</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Dismiss button */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleDismiss}
            style={{
              padding: 'var(--spacing-sm) var(--spacing-xl)',
              fontSize: 14,
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 1,
              background: `linear-gradient(180deg, ${GOLD} 0%, #a08030 50%, #8b6914 100%)`,
              border: `2px solid ${GOLD}`,
              borderRadius: 4,
              color: INK,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            }}
          >
            Huzzah!
          </button>
        </div>

        {/* Date */}
        <div
          style={{
            textAlign: 'center',
            marginTop: 'var(--spacing-md)',
            fontSize: 11,
            fontFamily: 'var(--font-display)',
            color: INK,
            opacity: 0.5,
          }}
        >
          {latestEntry.date}
        </div>
      </div>
    </div>
  );
}

